import React, { useState, useEffect } from "react";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/system";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts";
import AOS from 'aos';
import 'aos/dist/aos.css';

import { ReactTyped } from 'react-typed';

const MarksContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",

  "& .mark": {
    fontSize: "10px", // Adjust the font size as needed
  },
});

const LoanAmountSlider = () => {
  const [loanAmount, setLoanAmount] = useState(5000);
  const [interestRate, setInterestRate] = useState(5);
  const [loanTenureOption, setLoanTenureOption] = useState("years");
  const [loanTenureYears, setLoanTenureYears] = useState(15);
  const [loanTenureMonths, setLoanTenureMonths] = useState(180);
  const [emiYear, setEmiYear] = useState(0);
  const [emiMonth, setEmiMonth] = useState(0);
  const [totalInterestPayableYear, setTotalInterestPayableYear] = useState(0);
  const [totalInterestPayableMonth, setTotalInterestPayableMonth] = useState(0);
  const [totalpaymentYear, setTotalIpaymentYear] = useState(0);
  const [totalpaymenteMonth, setTotalpaymenteMonth] = useState(0);
  const [totalInterestPayablepercentage, setTotalInterestPayablepercetage] =
    useState(0);
  const [Total_amount_perceentage, setTotal_amount_percentage] = useState(0);

  const [chartWidth, setChartWidth] = useState(window.innerWidth < 760 ? 200 : 400);

  useEffect(() => {
    const handleResize = () => {
      setChartWidth(window.innerWidth < 760 ? 300 : 400);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
    AOS.init({ duration: 1000 });
    return () => {
      AOS.refresh();
    };
  }, []);

  useEffect(() => {
    let emiCalc;
    if (loanTenureOption === "years") {
      const r = interestRate / (12 * 100);
      const n = loanTenureYears * 12;
      emiCalc =
        (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setEmiYear(Math.round(emiCalc)); // Round emiYear to nearest integer
    } else if (loanTenureOption === "months") {
      const r = interestRate / (12 * 100);
      const n = loanTenureMonths;
      emiCalc =
        (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setEmiMonth(Math.round(emiCalc)); // Round emiMonth to nearest integer
    }
  }, [
    loanAmount,
    interestRate,
    loanTenureYears,
    loanTenureMonths,
    loanTenureOption,
  ]);

  useEffect(() => {
    if (loanTenureOption === "years") {
      const r = interestRate / (12 * 100);
      const n = loanTenureYears * 12;
      const emiCalc =
        (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setEmiYear(Math.round(emiCalc));
      const totalInterestPayableYear = emiCalc * n - loanAmount;
      setTotalInterestPayableYear(Math.round(totalInterestPayableYear)); // Round totalInterestPayableYear to nearest integer
    } else if (loanTenureOption === "months") {
      const r = interestRate / (12 * 100);
      const n = loanTenureMonths;
      const emiCalc =
        (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setEmiMonth(Math.round(emiCalc));
      const totalInterestPayableMonth = emiCalc * n - loanAmount;
      setTotalInterestPayableMonth(Math.round(totalInterestPayableMonth)); // Round totalInterestPayableMonth to nearest integer
    }
  }, [
    loanAmount,
    interestRate,
    loanTenureYears,
    loanTenureOption,
    loanTenureMonths,
  ]);

  useEffect(() => {
    if (loanTenureOption === "years") {
      const P = Number(loanAmount) + Number(totalInterestPayableYear);
      const totalpaymentYear = Math.round(P); // Round totalpaymentYear to nearest integer
      setTotalIpaymentYear(totalpaymentYear);
    } else if (loanTenureOption === "months") {
      const P = Number(loanAmount) + Number(totalInterestPayableMonth);
      const totalpaymenteMonth = Math.round(P); // Round totalpaymenteMonth to nearest integer
      setTotalpaymenteMonth(totalpaymenteMonth);
    }
  }, [loanAmount, totalInterestPayableMonth, totalInterestPayableYear]);

  useEffect(() => {
    if (loanTenureOption === "years") {
      const M = Number(totalpaymentYear);
      const Intrest_percentage = Math.round(
        (Number(totalInterestPayableYear) / M) * 100
      );
      const Total_percentage = Math.round((Number(loanAmount) / M) * 100);

      setTotalInterestPayablepercetage(Intrest_percentage.toFixed(2));
      setTotal_amount_percentage(Total_percentage.toFixed(2));
    } else if (loanTenureOption === "months") {
      const M = Number(totalpaymenteMonth);
      const Intrest_percentage = Math.round(
        (Number(totalInterestPayableMonth) / M) * 100
      );
      const Total_percentage = Math.round((Number(loanAmount) / M) * 100);

      setTotalInterestPayablepercetage(Intrest_percentage);
      setTotal_amount_percentage(Total_percentage);
    }
  }, [
    loanTenureOption,
    totalpaymentYear,
    totalpaymenteMonth,
    totalInterestPayableYear,
    totalInterestPayableMonth,
    loanAmount,
  ]);

  const handleLoanAmountSliderChange = (event, newValue) => {
    setLoanAmount(newValue);
  };

  const handleLoanAmountInputChange = (event) => {
    setLoanAmount(event.target.value === "" ? "" : Number(event.target.value));
  };

  const handleLoanAmountBlur = () => {
    if (loanAmount < 1000) {
      setLoanAmount(1000);
    } else if (loanAmount > 50000) {
      setLoanAmount(50000);
    }
  };

  const handleInterestRateSliderChange = (event, newValue) => {
    setInterestRate(newValue);
  };

  const handleInterestRateInputChange = (event) => {
    setInterestRate(
      event.target.value === "" ? "" : Number(event.target.value)
    );
  };

  const handleInterestRateBlur = () => {
    if (interestRate < 0) {
      setInterestRate(0);
    } else if (interestRate > 20) {
      setInterestRate(20);
    }
  };

  const handleLoanTenureOptionChange = (option) => {
    setLoanTenureOption(option);
  };

  const handleLoanTenureYearsSliderChange = (event, newValue) => {
    setLoanTenureYears(newValue);
    setLoanTenureMonths(newValue * 12); // Update months when years change
  };

  const handleLoanTenureMonthsSliderChange = (event, newValue) => {
    setLoanTenureMonths(newValue);
    setLoanTenureYears(newValue / 12); // Update years when months change
  };

  const handleLoanTenureYearsInputChange = (event) => {
    setLoanTenureYears(
      event.target.value === "" ? "" : Number(event.target.value)
    );
    setLoanTenureMonths(
      event.target.value === "" ? "" : Number(event.target.value) * 12
    );
  };

  const handleLoanTenureMonthsInputChange = (event) => {
    setLoanTenureMonths(
      event.target.value === "" ? "" : Number(event.target.value)
    );
    setLoanTenureYears(
      event.target.value === "" ? "" : Number(event.target.value) / 12
    );
  };

  const loanAmountMarks = [
    {
      value: 10000,
      label: "$10000",
    },
    {
      value: 20000,
      label: "$20000",
    },
    {
      value: 30000,
      label: "$30000",
    },
    {
      value: 50000,
      label: "$50000",
    },
  ];

  const interestRateMarks = [
    {
      value: 5,
      label: "5%",
    },
    {
      value: 10,
      label: "10%",
    },
    {
      value: 15,
      label: "15%",
    },
    {
      value: 20,
      label: "20%",
    },
  ];

  const data = [
    { id: 0, value: Total_amount_perceentage, label: "Total Amount "  },
    { id: 1, value: totalInterestPayablepercentage, label: "Total Intrest" },
  ];

  const size = {
    width: 400,
    height: 200,
  };

  return (
    <div className="Loan_main_div mb-4 mt-3">
      <div className="fw-semibold text-center font_size_loan_calcuator  hieght_fixed">
      <ReactTyped
        strings={['LOAN CALCULATOR']}
        typeSpeed={30}
        loop={false}
        showCursor={false} // Hide the cursor
      />
      </div>
      <div className=" ">
        <div className="Loan_main_divSub container width_main_container border_color box_shadow_card_loan   main_div_loan" data-aos="fade-up">
          <div className="container  ">
            <div className="d-flex justify-content-between slider-container">
              <div className="">
                <Typography variant="h5" gutterBottom>
                  Loan Amount:
                </Typography>
              </div>
              <div className="">
                <Input
                  value={loanAmount}
                  onChange={handleLoanAmountInputChange}
                  onBlur={handleLoanAmountBlur}
                  inputProps={{
                    step: 1000,
                    min: 1000,
                    max: 50000,
                    type: "number",
                  }}
                  className="fs_size"
                  startAdornment={
                    <InputAdornment position="start">$</InputAdornment>
                  }
                />
              </div>
            </div>
            <div className="slider-container">
              <Slider
                value={typeof loanAmount === "number" ? loanAmount : 0}
                className="Slider"
                onChange={handleLoanAmountSliderChange}
                min={1000}
                max={50000}
                step={1000}
                marks={loanAmountMarks}
                valueLabelDisplay="auto"
              />
            </div>

            <div className="d-flex justify-content-between slider-container mt-3">
              <div>
                <Typography variant="h5" gutterBottom>
                  Interest Rate:
                </Typography>
              </div>
              <div className="">
                <Input
                  value={interestRate}
                  onChange={handleInterestRateInputChange}
                  onBlur={handleInterestRateBlur}
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 20,
                    type: "number",
                  }}
                  className="fs_size fw-semi-bold"
                  startAdornment={
                    <InputAdornment position="start">%</InputAdornment>
                  }
                />
              </div>
            </div>
            <div className="slider-container mb-2">
              <Slider
                value={typeof interestRate === "number" ? interestRate : 0}
                onChange={handleInterestRateSliderChange}
                min={0}
                max={20}
                step={1}
                className="Sliderss"
                marks={interestRateMarks}
                valueLabelDisplay="auto"
              />
            </div>
            <Typography variant="h5" gutterBottom>
              Loan Tenure:
            </Typography>
            <div className="d-flex justify-content-start align-items-center gap-3 my-2 ">
              <button
                onClick={() => handleLoanTenureOptionChange("years")}
                className="button_years_months"
              >
                Years
              </button>
              <button
                onClick={() => handleLoanTenureOptionChange("months")}
                className="button_years_months"
              >
                Months
              </button>
            </div>
            {loanTenureOption === "years" ? (
              <div>
                <Input
                  value={loanTenureYears}
                  onChange={handleLoanTenureYearsInputChange}
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 30,
                    type: "number",
                  }}
                  className="fs_size fw-semi-bold"
                />
                <div className="slider-container mt-2">
                  <Slider
                    value={
                      typeof loanTenureYears === "number" ? loanTenureYears : 0
                    }
                    onChange={handleLoanTenureYearsSliderChange}
                    min={0}
                    max={20}
                    step={1}
                    className="Slider_months"
                    valueLabelDisplay="auto"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <Input
                  value={loanTenureMonths}
                  onChange={handleLoanTenureMonthsInputChange}
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 30,
                    type: "number",
                  }}
                  className="fs_size fw-semi-bold"
                />
                <div className="slider-container mt-2">
                  <Slider
                    value={
                      typeof loanTenureMonths === "number"
                        ? loanTenureMonths
                        : 0
                    }
                    onChange={handleLoanTenureMonthsSliderChange}
                    min={0}
                    max={30}
                    step={1}
                    className="Slider_months"
                    valueLabelDisplay="auto"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Student-after-calcuation*/}
          <div className=" container d-flex ">
            <div className="row">
            <div className="col-md-4 margin_bottom_chart_media">
              <div className="text-center">
                <div className="Text_title">Loan EMI</div>
                <div className="Text_calculation">
                  {loanTenureOption === "years" && (
                    <div className="Loan_Emi_year">${emiYear}</div>
                  )}
                  {loanTenureOption === "months" && (
                    <div className="Loan_Emi_month">${emiMonth}</div>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className="Text_title">Total InterestPayable</div>
                <div className="Text_calculation">
                  {" "}
                  {loanTenureOption === "years" && (
                    <div className="Total_INtrest_Year">
                      ${totalInterestPayableYear}
                    </div>
                  )}
                  {loanTenureOption === "months" && (
                    <div className="Total_INtrest_month">
                      ${totalInterestPayableMonth}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className="Text_title"> Total Payment</div>
                <div className="Text_calculation">
                  {loanTenureOption === "years" && (
                    <div className="Total_payment_Year">
                      ${totalpaymentYear}
                    </div>
                  )}
                  {loanTenureOption === "months" && (
                    <div className="Total_INtrest_month">
                      ${totalpaymenteMonth}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="margin_top_chart_margin">
              <PieChart
  series={[
    {
      data,
      highlightScope: { faded: "global", highlighted: "item" },
      faded: {
        innerRadius: 50, // Increased to add space
        additionalRadius: -30,
        color: "gray",
      },
      stroke: "#000000", // This adds a black border
      strokeWidth: 2,    // This defines the border width
    },
  ]}
  height={200}
  width={chartWidth}
  style={{ fontSize: "10px" }} // Correct way to set fontSize
/>



              </div>
           
            </div>
            </div>
         
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanAmountSlider;
